import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToastController } from '@freshworks/crayons/react';
import { useRecordsPage, useMessageChannelAdapter, useBreadcrumbRouteMetaEmitter } from 'hooks';
import { useFreshdeskContextState } from './freshdesk-context';
import { recordPageController } from 'controllers';
import { LEGO_CONSTANTS } from 'constants/index';
import { getBrokenFieldIds } from 'utils';
import { getEntityRouteParam } from 'utils/entityIdentifier';

const initialContextValue = {
  loading: true,
  entity: null,
  associatedEntities: [],
  record: null,
  error: null,
  recordsListData: {},
  nativeDropdownChoicesByFieldId: new Map(),
};

export const RecordPageStateContext = React.createContext(initialContextValue);
const RecordPageUpdaterContext = React.createContext();

const RecordPageProvider = props => {
  const { state } = useLocation();
  const { fetchRecordAndEntityIfNull, fetchRecord: getRecord } = useRecordsPage();
  const { isFeatureEnabled } = useFreshdeskContextState();
  const { getNativeDropdownChoices } = useMessageChannelAdapter();
  const useDropdownLookup = isFeatureEnabled('useDropdownLookup');
  // The actual native-dropdown-choices API call is made by the host application over the message channel
  // (see useMessageChannelAdapter) - recordPageController only owns building filter params and mapping the
  // response, so it's handed the host-backed fetcher here rather than calling a REST service itself.
  recordPageController.nativeDropdownDataSource = { getNativeDropdownChoices };
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Kept in a ref so loadRecordPage's identity (and with it the load effect) doesn't change when these do
  const redirectToBuilderRef = useRef(null);
  redirectToBuilderRef.current = brokenEntity => {
    ToastController().trigger({ type: 'error', content: t('createEntity.brokenField.recordsBlocked') });
    navigate(`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/edit/${getEntityRouteParam(brokenEntity)}`, { replace: true });
  };
  const { entity, associatedEntities, data: recordsListData } = state ?? { entity: null, associatedEntities: [] };
  const [recordPage, setRecordPage] = useState({ ...initialContextValue, entity, associatedEntities, recordsListData });
  const { displayId } = useParams();
  useBreadcrumbRouteMetaEmitter({
    entity: recordPage.entity,
    record: recordPage.record,
    displayId,
  });

  const fetchRecord = useCallback(async () => {
    setRecordPage(prev => {
      return {
        ...prev,
        loading: true,
      };
    });
    const recordData = await getRecord(recordPage.entity);
    setRecordPage(prev => {
      return {
        ...prev,
        ...recordData,
        loading: false,
      };
    });
  }, [recordPage.entity, getRecord]);

  const loadRecordPage = useCallback(
    async isCancelled => {
      setRecordPage(prev => {
        return {
          ...prev,
          loading: true,
        };
      });

      try {
        const entityRecordData = await fetchRecordAndEntityIfNull(entity);
        if (isCancelled()) return;
        const { deletedRelatedEntityIds, ...recordData } = entityRecordData ?? {};
        const resolvedEntity = recordData.entity ?? entity;
        let nativeDropdownChoicesByFieldId = new Map();
        let loadedDropdownChoices = null;

        if (useDropdownLookup && resolvedEntity?.fields) {
          try {
            const choicesResponse = await recordPageController.fetchNativeDropdownChoices(resolvedEntity.fields);
            if (isCancelled()) return;
            if (choicesResponse?.success) {
              nativeDropdownChoicesByFieldId = choicesResponse.response;
              loadedDropdownChoices = choicesResponse.response;
            }
          } catch (error) {
            console.error('Error fetching native dropdown choices - ' + error);
          }
        }

        // A field whose source/target was deleted blocks the records pages: send the user to the builder to delete it.
        if (resolvedEntity?.fields && getBrokenFieldIds(resolvedEntity.fields, { choicesByFieldId: loadedDropdownChoices, deletedRelatedEntityIds }).size > 0) {
          redirectToBuilderRef.current(resolvedEntity);
          return;
        }

        setRecordPage(prev => ({
          ...prev,
          ...recordData,
          nativeDropdownChoicesByFieldId,
          loading: false,
        }));
      } catch (error) {
        if (isCancelled) return;
        console.error('Error loading record page - ' + error);
        setRecordPage(prev => ({
          ...prev,
          error,
          loading: false,
        }));
      }
    },
    [entity, fetchRecordAndEntityIfNull, useDropdownLookup]
  );

  useEffect(() => {
    let cancelled = false;
    loadRecordPage(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [loadRecordPage]);

  const updateFunctions = useMemo(() => {
    return {
      fetchRecord,
      setRecordPage,
    };
  }, [fetchRecord, setRecordPage]);

  return (
    <RecordPageStateContext.Provider value={recordPage}>
      <RecordPageUpdaterContext.Provider value={updateFunctions}>{props.children}</RecordPageUpdaterContext.Provider>
    </RecordPageStateContext.Provider>
  );
};

const useRecordPageState = () => {
  const recordPageState = useContext(RecordPageStateContext);
  if (!recordPageState) {
    throw new Error('useRecordPageState must be used within a RecordPageProvider');
  }
  return recordPageState;
};

const useRecordPageUpdater = () => {
  const { setRecordPage, fetchRecord } = useContext(RecordPageUpdaterContext);
  if (!setRecordPage || !fetchRecord) {
    throw new Error('useRecordPageUpdater must be used within a RecordPageProvider');
  }

  return { setRecordPage, fetchRecord };
};

export { RecordPageProvider, useRecordPageState, useRecordPageUpdater };
